import { type Except, typeUtils } from '@festina-life/core'
import cx from 'classnames'
import {
  Formik,
  type FormikConfig,
  type FormikHelpers,
  type FormikProps,
} from 'formik'
import {
  type ReactNode,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { AnyObject, LazySchema, Maybe, ObjectSchema } from 'yup'

import { FormikChildListenerProvider } from '../../forms/context/FormikChildListenerProvider'
import { useOptionalUnsavedChangesContext } from '../../forms/context/unsavedChangesContext'
import { useFormikContext } from '../../hooks/useFormikContext'
import { type SemanticSize, spacingStyles } from '../../libs/spacingStyles'
import { useDataContext } from '../../theme/dataContext'
import { isSchemaObjectDescription } from '../formikUtils/formikUtils'
import { sanitizeFormData } from './formikFormUtils'
import { useFocusFirstAutofocusField } from './useFocusFirstAutofocusField'

export type SubmitStatus = 'submitting' | 'success' | Error

export type FormikFormProps<T extends object> = Except<
  FormikConfig<T>,
  'onSubmit' | 'children' | 'validationSchema'
> & {
  validationSchema?:
    | ObjectSchema<Maybe<AnyObject>>
    | ObjectSchema<Maybe<AnyObject>, null, Maybe<AnyObject>>
    | LazySchema<unknown>
  onStatusChanged?: (status: SubmitStatus) => void
  onInputChange?: (values: T) => void
  onErrorChange?: (error: boolean) => void
  onSubmit?: (
    values: T,
    formikHelpers: FormikHelpers<T>,
  ) => void | Promise<unknown>
  autoComplete?: boolean
  /** id used in the <form> */
  formId?: string
  children: ((props: FormikProps<T>) => ReactNode) | ReactNode
  /* Used to bubble-up the trigger to revalidate the form. */
  onSetRevalidateForm?: (onRevalidateForm: () => Promise<boolean>) => void
  /**
   * Whether the form has a validation that could take effect prior to submit.
   *
   * A form can have a client-side validation object even if this validation is not active.
   * */
  onFormHasValidationChanged?: (hasValidation: boolean) => void
  shouldSanitizeOnSubmit?: boolean
  shouldFieldsBeDisabled?: boolean
  isValidationDisabled?: boolean
  className?: string
  gap?: SemanticSize
  shouldShowChangesInForm?: boolean
  shouldSubmitOnMount?: boolean
  /** Used to expose Formik's submitForm imperatively to parent flows. */
  onSetSubmitForm?: (submitForm: (() => Promise<unknown>) | undefined) => void
}

// Note that the isSubmitting will only be true when the onSubmit has been given the function explicit. Meaning it needs to be onSubmit={someFunc} and not onSubmit={(values) => someFunc(values)}
export const FormikForm = <T extends object>({
  onStatusChanged,
  onInputChange,
  onErrorChange,
  onFormHasValidationChanged,
  children,
  onSubmit = () => {},
  autoComplete = false,
  formId,
  onSetRevalidateForm,
  shouldSanitizeOnSubmit = true,
  shouldFieldsBeDisabled = false,
  initialStatus,
  initialValues,
  className,
  gap = 'spacious',
  shouldShowChangesInForm,
  shouldSubmitOnMount,
  onSetSubmitForm,
  ...otherProps
}: FormikFormProps<T>) => {
  const [success, setSuccess] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  useFocusFirstAutofocusField(formRef)

  // It's safe to assume that `onSubmit` is defined here
  // since it has a default value assigned to it.
  //
  // There is no need to wrap the original `onSubmit` if `onSubmitStatus`
  // was not provided. The `formikProps` could be used for `isSubmitting`,
  // but not for `error`, so it's simpler to handle both here.
  const handleSubmit = async (
    ...args: Parameters<NonNullable<typeof onSubmit>>
  ) => {
    const [values, ...rest] = args
    const data = shouldSanitizeOnSubmit ? sanitizeFormData(values) : values

    onStatusChanged?.('submitting')

    try {
      await onSubmit(data ?? ({} as T), ...rest)
      onStatusChanged?.('success')
      // Due to DirtyChangeListener isSubmitting goes from true to false
      // Which means after the form is submitted, the dirty state will be set to true again. So we need to set success to true to avoid that.
      setSuccess(true)
    } catch (e) {
      onStatusChanged?.(typeUtils.isError(e) ? e : new Error(`${e}`))
      throw e
    }
  }

  const { data } = useDataContext<T>()

  const yupValidationSchemaFields = useMemo(() => {
    const description = otherProps.validationSchema?.describe()

    return isSchemaObjectDescription(description)
      ? description.fields
      : undefined
  }, [otherProps.validationSchema])

  const formHasValidation =
    !shouldFieldsBeDisabled && !!otherProps.validationSchema
  useEffect(
    () => onFormHasValidationChanged?.(formHasValidation),
    [onFormHasValidationChanged, formHasValidation],
  )

  return (
    <Formik<T>
      onSubmit={handleSubmit}
      initialStatus={{
        yupFields: yupValidationSchemaFields,
        shouldFieldsBeDisabled,
        shouldShowChangesInForm: false,
        ...(initialStatus ?? {}),
      }}
      initialValues={data ?? initialValues}
      {...otherProps}
      validationSchema={
        shouldFieldsBeDisabled || otherProps.isValidationDisabled
          ? undefined
          : otherProps.validationSchema
      }
    >
      {(formikProps) => (
        <form
          ref={formRef}
          onSubmit={formikProps.handleSubmit}
          autoComplete={autoComplete ? 'on' : 'off'}
          className={cx(
            className,
            'flex flex-col',
            spacingStyles({ gap, padding: 'none' }),
          )}
          id={formId}
        >
          <FormikChildListenerProvider>
            {onSetRevalidateForm && (
              <ValidateFormListener onSetRevalidateForm={onSetRevalidateForm} />
            )}
            <DirtyChangeListener
              dirty={formikProps.dirty && !success}
              isSubmitting={formikProps.isSubmitting}
            />
            {onInputChange && (
              <InputChangeListener
                onInputChange={onInputChange}
                values={formikProps.values}
              />
            )}
            {shouldSubmitOnMount && <FormikHandleInitialSearch />}
            {onSetSubmitForm && (
              <FormikSubmitFormListener onSetSubmitForm={onSetSubmitForm} />
            )}
            {onErrorChange && (
              <ErrorChangeListener onErrorChange={onErrorChange} />
            )}
            <ValidationChangeListener
              isValidationDisabled={otherProps.isValidationDisabled}
            />
            <ShowFormChangesListener
              shouldShowChangesInForm={shouldShowChangesInForm}
            />
            {typeof children === 'function' ? children(formikProps) : children}
          </FormikChildListenerProvider>
        </form>
      )}
    </Formik>
  )
}

type DirtyChangeListenerProp = {
  dirty: boolean
  isSubmitting: boolean
}

const DirtyChangeListener = ({
  dirty,
  isSubmitting,
}: DirtyChangeListenerProp) => {
  const { onIsDirtyChanged, onIsSubmittingChanged } =
    useOptionalUnsavedChangesContext()

  useEffect(() => {
    onIsDirtyChanged?.(dirty)
  }, [dirty, onIsDirtyChanged])

  useEffect(() => {
    onIsSubmittingChanged?.(isSubmitting)
  }, [isSubmitting, onIsSubmittingChanged])

  return null
}

type InputChangeListenerProp<T> = {
  onInputChange: (values: T) => void
  values: T
}

const InputChangeListener = <T,>({
  onInputChange,
  values,
}: InputChangeListenerProp<T>) => {
  useEffect(() => {
    onInputChange(values)
    // oxlint-disable-next-line exhaustive-deps
  }, [values])

  return null
}

const ValidateFormListener = ({
  onSetRevalidateForm,
}: {
  onSetRevalidateForm: NonNullable<
    FormikFormProps<object>['onSetRevalidateForm']
  >
}) => {
  const { validateForm, setStatus, status } =
    useFormikContext<Record<string, unknown>>()

  useEffect(() => {
    onSetRevalidateForm(async () => {
      const errors = await validateForm()
      setStatus({ ...status, hasFormValidated: true })

      return Object.keys(errors)?.length > 0
    })
  }, [onSetRevalidateForm, validateForm, status, setStatus])

  return null
}

const ErrorChangeListener = ({
  onErrorChange,
}: {
  onErrorChange: (error: boolean) => void
}) => {
  const { errors } = useFormikContext<Record<string, unknown>>()

  useEffect(() => {
    onErrorChange(errors && Object.keys(errors)?.length > 0)
    // oxlint-disable-next-line exhaustive-deps
  }, [errors])

  return null
}

const ShowFormChangesListener = ({
  shouldShowChangesInForm,
}: {
  shouldShowChangesInForm?: boolean
}) => {
  const { setStatus, status } = useFormikContext<Record<string, unknown>>()

  const handleShouldSetChanges = useEffectEvent(() => {
    setStatus({
      ...status,
      shouldShowChangesInForm: !!shouldShowChangesInForm,
    })
  })

  useEffect(() => {
    if (status.shouldShowChangesInForm === shouldShowChangesInForm) {
      return
    }

    handleShouldSetChanges()
  }, [shouldShowChangesInForm, status.shouldShowChangesInForm])

  return null
}

const ValidationChangeListener = ({
  isValidationDisabled,
}: {
  isValidationDisabled?: boolean
}) => {
  const hasValidationEverBeenDisabled = useRef(!!isValidationDisabled)
  const { status, setStatus, validateForm, setErrors } =
    useFormikContext<Record<string, unknown>>()

  useEffect(() => {
    hasValidationEverBeenDisabled.current ||= !!isValidationDisabled
  }, [isValidationDisabled])

  useEffect(() => {
    if (hasValidationEverBeenDisabled.current) {
      void (async () => {
        setErrors(await validateForm())
        setStatus({ ...status, hasFormValidated: true })
      })()
    }
    // Ideally we would rely on validationSchema changing.
    // But that's not safe because it's not always memo'd.
    // oxlint-disable-next-line exhaustive-deps
  }, [isValidationDisabled])

  return null
}

const FormikHandleInitialSearch = () => {
  const { submitCount, submitForm } = useFormikContext()
  const hasSubmittedRef = useRef(false)

  useEffect(() => {
    if (!hasSubmittedRef.current && submitCount === 0) {
      hasSubmittedRef.current = true
      void submitForm()
    }
  }, [submitCount, submitForm])

  return null
}

const FormikSubmitFormListener = ({
  onSetSubmitForm,
}: {
  onSetSubmitForm: NonNullable<FormikFormProps<object>['onSetSubmitForm']>
}) => {
  const { submitForm } = useFormikContext<Record<string, unknown>>()

  useEffect(() => {
    onSetSubmitForm(submitForm)

    return () => {
      onSetSubmitForm(undefined)
    }
  }, [onSetSubmitForm, submitForm])

  return null
}
